/**
 * LeadershipCard — Q4 of the Nov 2026 form: does the employee perform in line with all three
 * Leadership Values consistently? Yes/No per value (set up like the behaviors, Zohal Oct 29),
 * then "what went well" / "what could be improved", structured to SBIR like a deliverable.
 * Only shown when the manager answered "Yes" to "Does X have direct reports?" at the top of the tab.
 */
import { C, LEADERSHIP_VALUES, getLeadershipValueGroup, getTrack } from '../../data/managerReviewData';
import type { Deliverable, Employee, FeedbackItem, LeadershipData } from '../../types/managerReviewV2';
import ReviewTextarea from './ReviewTextarea';
import { LEADERSHIP_CONTEXT, LEADERSHIP_VALUE_NAMES, answerPoint, copyMention, leadershipMentions } from './leadershipValues';

interface LeadershipCardProps {
  employee: Employee;
  level: string;
  data: LeadershipData;
  deliverables: Deliverable[];
  onChange: (update: (prev: LeadershipData) => LeadershipData) => void;
  selfReview: string;
  feedbackItems: FeedbackItem[];
  perfFeedbackRaw?: string;
  anytimeFeedbackRaw?: string;
}

const renderBold = (text: string): React.ReactNode => {
  if (!text || !text.includes('**')) return text;
  return text.split('**').map((p, i) => (i % 2 === 1 ? <strong key={i}>{p}</strong> : p));
};

/** The level's full expectation for one value, one point per line (four per value in the framework). */
function valuePoints(level: string, name: string): string[] {
  const group = getTrack(level) === 'M' ? getLeadershipValueGroup(level) : 'M5';
  return (LEADERSHIP_VALUES[group]?.[name] || '').split('\n').map(l => l.trim()).filter(Boolean);
}

export default function LeadershipCard({
  employee, level, data, deliverables, onChange, selfReview, feedbackItems, perfFeedbackRaw, anytimeFeedbackRaw,
}: LeadershipCardProps) {
  const firstName = employee.name.split(' ')[0];
  const valueDescriptions = LEADERSHIP_VALUE_NAMES.map(v => `${v}: ${valuePoints(level, v).join(' ').replace(/\*\*/g, '')}`);
  const notMet = LEADERSHIP_VALUE_NAMES.filter(v => data.answers[v] === false);
  const mentions = leadershipMentions(deliverables, data);
  const aiContext = {
    employeeName: employee.name, role: employee.role, level, valueName: 'Leadership Values',
    availableBehaviors: valueDescriptions, selfReview, feedbackItems, perfFeedbackRaw, anytimeFeedbackRaw,
  };

  return (
    <div id="leadership-values" style={{
      background: '#fff', borderRadius: 10, padding: 24, marginBottom: 24,
      border: `1px solid ${C.border}`, boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
    }}>
      <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 4 }}>Leadership Values</div>
      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>{LEADERSHIP_CONTEXT}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, lineHeight: 1.5, marginBottom: 16 }}>
        Does {firstName} perform in line with all three Leadership Values consistently?
      </div>

      <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
        {LEADERSHIP_VALUE_NAMES.map((v, i) => {
          const points = valuePoints(level, v);
          return (
            <div key={v} style={{
              padding: '10px 14px',
              borderBottom: i < LEADERSHIP_VALUE_NAMES.length - 1 ? `1px solid ${C.border}` : 'none',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>{v}</div>
              {points.map((p, j) => {
                const answer = data.pointAnswers?.[`${v}#${j}`];
                return (
                  <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0' }}>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: '#555', lineHeight: 1.5 }}>{renderBold(p)}</div>
                    {([true, false] as const).map(val => {
                      const active = answer === val;
                      const col = val ? C.green : C.red;
                      return (
                        <button
                          key={String(val)}
                          type="button"
                          aria-pressed={active}
                          aria-label={`${v}, point ${j + 1}: ${val ? 'Yes' : 'No'}`}
                          onClick={() => onChange(prev => answerPoint(prev, v, j, points.length, val))}
                          style={{
                            minWidth: 48, padding: '5px 9px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                            cursor: 'pointer', fontFamily: 'Inter', flexShrink: 0,
                            border: `1.5px solid ${active ? col : '#d1d5db'}`,
                            background: active ? col : '#fff', color: active ? '#fff' : '#555',
                          }}
                        >{val ? 'Yes' : 'No'}</button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {mentions.map(m => (
        <div key={m.id} style={{ marginBottom: 12, padding: '10px 12px', background: '#f5f9ff', borderRadius: 8, borderLeft: `3px solid ${C.blue}`, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, fontSize: 12, color: '#444', lineHeight: 1.6 }}>
            You tagged <strong>{m.values.join(', ')}</strong> on "{m.title}" in Delivery. That's relevant here too.
          </div>
          <button
            type="button"
            onClick={() => onChange(prev => copyMention(prev, m))}
            style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.blue}`, background: '#fff', color: C.blue, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter', whiteSpace: 'nowrap' }}
          >Copy notes here</button>
        </div>
      ))}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 6 }}>What went well?</div>
          <ReviewTextarea
            value={data.wellDone}
            onChange={v => onChange(prev => ({ ...prev, wellDone: v }))}
            placeholder="Where did they live the Leadership Values? Type or hit Record to speak your thoughts."
            voice
            aiContext={{ ...aiContext, field: 'strengths' }}
            onRedirected={text => onChange(prev => ({ ...prev, improvement: (prev.improvement ? prev.improvement + '\n\n' : '') + text }))}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 6 }}>What could be improved?</div>
          <ReviewTextarea
            value={data.improvement}
            onChange={v => onChange(prev => ({ ...prev, improvement: v }))}
            placeholder="Where could they have been stronger as a leader? Type or hit Record to speak your thoughts."
            voice
            aiContext={{ ...aiContext, field: 'improvements', notObservedBehaviors: notMet }}
          />
        </div>
      </div>
    </div>
  );
}
